const DEFAULT_PREFIX = "paws-model-";

export class ModelNotFoundError extends Error {
  constructor(url) {
    super(`Modello non trovato: ${url}`);
    this.name = "ModelNotFoundError";
  }
}

export class ModelLoader {
  constructor(baseUrl, { variant = "auto", cachesApi = globalThis.caches, fetcher = (...args) => fetch(...args),
    cachePrefix = DEFAULT_PREFIX } = {}) {
    this.baseUrl = baseUrl;
    this.variant = variant;
    this.cachesApi = cachesApi;
    this.fetcher = fetcher;
    this.cachePrefix = cachePrefix;
  }

  async loadMetadata() {
    const url = `${this.baseUrl}/metadata.json`;
    let response;
    try {
      response = await this.fetcher(url, { cache: "no-cache" });
    } catch (error) {
      const cached = await this.#cachedMetadata();
      if (cached) return cached;
      throw error;
    }
    if (response.status === 404 || response.status === 401) throw new ModelNotFoundError(url);
    if (!response.ok) throw new Error(`Metadata HTTP ${response.status}`);
    const metadata = await response.json();
    this.#validate(metadata);
    await this.#storeMetadata(metadata);
    return metadata;
  }

  pickVariant(metadata) {
    const variants = metadata.variants || { fp32: "model.onnx" };
    if (this.variant !== "auto" && variants[this.variant]) return variants[this.variant];
    return variants.int8 || variants.fp32 || Object.values(variants)[0];
  }

  async loadModelBytes(metadata, onProgress = () => {}) {
    const file = this.pickVariant(metadata);
    const url = `${this.baseUrl}/${file}`;
    const cacheName = `${this.cachePrefix}${metadata.model_version}`;
    const cache = this.cachesApi ? await this.cachesApi.open(cacheName) : null;
    const hit = cache ? await cache.match(url) : null;
    if (hit) {
      onProgress(100);
      return new Uint8Array(await hit.arrayBuffer());
    }
    const response = await this.fetcher(url);
    if (response.status === 404) throw new ModelNotFoundError(url);
    if (!response.ok) throw new Error(`Modello HTTP ${response.status}`);
    const bytes = await this.#readWithProgress(response, onProgress);
    if (cache) {
      await cache.put(url, new Response(bytes, { headers: { "Content-Type": "application/octet-stream" } }));
      await this.#purgeOldCaches(cacheName);
    }
    return bytes;
  }

  async #readWithProgress(response, onProgress) {
    const total = Number(response.headers.get("Content-Length")) || 0;
    if (!response.body || !total) {
      const buffer = new Uint8Array(await response.arrayBuffer());
      onProgress(100);
      return buffer;
    }
    const reader = response.body.getReader();
    const chunks = [];
    let received = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.length;
      onProgress(Math.min(99, Math.round((received / total) * 100)));
    }
    const bytes = new Uint8Array(received);
    let offset = 0;
    chunks.forEach((chunk) => { bytes.set(chunk, offset); offset += chunk.length; });
    onProgress(100);
    return bytes;
  }

  #validate(metadata) {
    const ok = metadata && Array.isArray(metadata.classes) && metadata.classes.length > 1 && metadata.input?.size;
    if (!ok) throw new Error("Metadata del modello non valido");
  }

  async #storeMetadata(metadata) {
    if (!this.cachesApi) return;
    const cache = await this.cachesApi.open(`${this.cachePrefix}meta`);
    await cache.put("metadata.json", new Response(JSON.stringify(metadata)));
  }

  async #cachedMetadata() {
    if (!this.cachesApi) return null;
    const cache = await this.cachesApi.open(`${this.cachePrefix}meta`);
    const hit = await cache.match("metadata.json");
    return hit ? hit.json() : null;
  }

  async #purgeOldCaches(keep) {
    const names = await this.cachesApi.keys();
    const stale = (name) => name.startsWith(this.cachePrefix) && name !== keep && name !== `${this.cachePrefix}meta`;
    await Promise.all(names.filter(stale).map((name) => this.cachesApi.delete(name)));
  }
}
